/**
 * Style definitions for the AlertPopup component.
 *
 * @module components/common/AlertPopup
 * @author Snowfly
 * @version 1.0.0
 */

/**
 * Represents a function that defines styles for an alert popup component.
 * The styles are dynamically applied based on the provided theme object
 * and specific conditions such as severity levels.
 *
 * @param {Object} theme - The theme object used to define the styles, typically containing
 *                         palette and color definitions for the application.
 * @returns {Object} An object representing the styles for the AlertPopup component.
 *
 * Properties:
 * - `alert`: Defines the main styles for the alert component, including positioning,
 *            padding, border, box shadow, and transformation.
 * - `alert & [severity="warning"]`: A nested style rule applied when the `severity` attribute
 *                                   is set to "warning". This rule applies specific colors
 *                                   for the background, text, and border based on the theme's
 *                                   warning palette.
 */
export const AlertPopup = (theme) => ({
    alert: {
        position: 'fixed',
        left: 'calc(100vw - 100% - 2em)',
        top: 'calc(100vh - 6rem)',
        zIndex: '100000',
        isolation: 'isolate',
        padding: '1em',
        borderWidth: '1px',
        borderStyle: 'solid',
        boxShadow: '2px 2px 6px 0 rgba(32, 32, 32, 0.5)',
        width: 'auto',
        transform: 'translateX(calc(100vw - 100% - 1rem))',

        '& [severity="warning"]': {
            backgroundColor: theme.palette.warning.light,
            color: theme.palette.warning.dark,
            borderColor: theme.palette.warning.dark,
        },
    },
});

export default AlertPopup;
